import { Util } from "../utils/util";

export interface IMfaAccountChallenge {
    id: string;
    profileId: number;
}

/**
 * TokenService provides methods for managing JWT and refresh tokens in local storage,
 * as well as updating them from HTTP response headers.
 */
export class TokenService {
    private static readonly jwtKey = "tk";
    private static readonly refreshTokenKey = "rtk";
    private static readonly mfaTrustCookie = "mfa_trust";
    private static readonly mfaSessionKey = "mfa_session";
    private static readonly mfaSessionHeader = "X-Mfa-Session";
    private static readonly mfaAccountChallengeKey = "mfa_account_challenge";
    private static readonly mfaTrustDays = 30;

    // The trust cookie is per browser and survives sign-out; the session token is per tab.
    public static getMfaTrustToken(): string | null {
        return Util.getCookie(this.mfaTrustCookie) || null;
    }

    // Not HttpOnly: the Loyalty API is cross-origin and takes the token in the sign-in body, so JS must read it.
    public static setMfaTrustToken(token: string): void {
        const expires = new Date(Date.now() + this.mfaTrustDays * 864e5).toUTCString();
        document.cookie = `${this.mfaTrustCookie}=${encodeURIComponent(token)}; expires=${expires}; path=/; Secure; SameSite=Strict`;
    }

    // Storage can throw (blocked site data, private mode, some webviews); treat that as no token so sign-in still works.
    public static getMfaSessionToken(): string | null {
        return this._trySessionStorage(() => sessionStorage.getItem(this.mfaSessionKey), null);
    }

    public static setMfaSessionToken(token: string): void {
        this._trySessionStorage(() => sessionStorage.setItem(this.mfaSessionKey, token), undefined);
    }

    public static removeMfaSessionToken(): void {
        this._trySessionStorage(() => sessionStorage.removeItem(this.mfaSessionKey), undefined);
    }

    // Only decides whether to prompt; the server still checks the signature before any gated change.
    public static hasMfaSessionFor(profileId: number): boolean {
        const token = this.getMfaSessionToken();
        if (!token)
            return false;

        try {
            const payload = JSON.parse(atob(JSON.parse(token).Data));
            return payload.ProfileId === profileId && Date.parse(payload.ExpiresUtc) > Date.now();
        } catch {
            return false;
        }
    }

    public static getMfaSessionHeaders(): Record<string, string> {
        const token = this.getMfaSessionToken();
        return token ? { [this.mfaSessionHeader]: token } : {};
    }

    // Kept per tab so a reload reuses the open challenge instead of spending another from the per-IP limit.
    public static getMfaAccountChallenge(): IMfaAccountChallenge | null {
        return this._trySessionStorage(() => JSON.parse(sessionStorage.getItem(this.mfaAccountChallengeKey) ?? "null"), null);
    }

    public static setMfaAccountChallenge(challenge: IMfaAccountChallenge): void {
        this._trySessionStorage(() => sessionStorage.setItem(this.mfaAccountChallengeKey, JSON.stringify(challenge)), undefined);
    }

    public static removeMfaAccountChallenge(): void {
        this._trySessionStorage(() => sessionStorage.removeItem(this.mfaAccountChallengeKey), undefined);
    }

    private static _trySessionStorage<T>(action: () => T, fallback: T): T {
        try {
            return action();
        } catch {
            return fallback;
        }
    }

    /**
     * Stores the JWT token in local storage.
     * @param {string} token - The JWT token to store.
     */
    public static setJWT(token: string): void {
        localStorage.setItem(this.jwtKey, token);
    }

    /**
     * Retrieves the JWT token from local storage.
     * @returns {string | null} The stored JWT token, or null if not present.
     */
    public static getJWT(): string | null {
        return localStorage.getItem(this.jwtKey);
    }

    /**
     * Removes the JWT token from local storage.
     * @returns {void}
     */
    public static removeJWT(): void {
        localStorage.removeItem(this.jwtKey);
    }

    /**
     * Stores the refresh token in local storage.
     * @param {string} token - The refresh token to store.
     */
    public static setRefreshToken(token: string): void {
        localStorage.setItem(this.refreshTokenKey, token);
    }

    /**
     * Retrieves the refresh token from local storage.
     * @returns {string | null} The stored refresh token, or null if not present.
     */
    public static getRefreshToken(): string | null {
        return localStorage.getItem(this.refreshTokenKey);
    }

    /**
     * Removes the refresh token from local storage.
     * @returns {void}
     */
    public static removeRefreshToken(): void {
        localStorage.removeItem(this.refreshTokenKey);
    }

    /**
     * Updates stored JWT and refresh tokens based on HTTP response headers.
     * If the response includes an 'Authorization' header or 'rtk' header, those values are persisted.
     *
     * @param {Response} response - The HTTP response containing token headers.
     */
    public static updateTokensFromHeaders(response: Response): void {
        if (response.headers.has('Authorization')) {
            const authHeader = response.headers.get('Authorization');
            const token = authHeader?.split(' ')[1] ?? "";
            if (token)
                this.setJWT(token);
        }

        if (response.headers.has('rtk')) {
            const refreshToken = response.headers.get('rtk');
            if (refreshToken)
                this.setRefreshToken(refreshToken);
        }
    }
}